// Components==============
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import storeProject from 'actions/project/store';
import Button from 'components/UI/Button/Button';
import Checkbox from 'components/UI/Checkbox/Checkbox';
import Dropdown from 'components/UI/Dropdown/Dropdown';
import LabelWrapper from 'components/UI/Label/LabelWrapper';
import Modal from 'components/UI/Modal/Modal';
import Spinner from 'components/UI/Spinner/Spinner';
import TooltipMenu from 'components/UI/Tooltip/TooltipMenu';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useState } from 'react';
import { ProjectCardContext } from '../../ProjectCard';
import './ThemeModal.module.scss';
// =========================

const TooltipMenuContent = () => {
  const translate = useTranslate();
  const dispatch = useReduxAction();
  const { theme } = useContext(ProjectCardContext);

  const names =
    theme?.allProjects?.map((p) =>
      p.author
        ? `${p.author.first_name} ${p.author.last_name}`
        : (translate('general.terms.no-name') as string)
    ) || [];

  const [isChecked, setIsChecked] = useState(false);
  const [selectedItem, setSelectedItem] = useState(names?.[0]);

  const project = theme?.allProjects?.find(
    (p) => `${p?.author?.first_name} ${p?.author?.last_name}` === selectedItem
  );

  function copyProjectWithProcess() {
    dispatch(
      storeProject({
        withProcess: isChecked,
        copyProject: true,
        projectId: project?.id || theme?.allProjects?.[0].id,
        themeId: theme?.id,
      })
    );
  }

  return (
    <div styleName="tooltip-content">
      <Checkbox isChecked={isChecked} setIsChecked={setIsChecked}>
        {translate('project.terms.including-process')}
      </Checkbox>
      <p>{translate('project.import-description')}</p>
      <Dropdown
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        initialOptions={names}
        initialText={names?.[0]}
        disabled={names.length <= 1 || !isChecked}
      />
      <div styleName="button">
        <Button onClick={copyProjectWithProcess}>
          {translate('general.terms.add')}
        </Button>
      </div>
    </div>
  );
};

export default function ThemeModal() {
  const {
    theme,
    setThemeModalIsOpen,
    categories,
    showProjectModal,
    themeModalRef,
  } = useContext(ProjectCardContext);

  const dispatch = useReduxAction();
  const userId = useRedux((state) => state.authentication?.user?.id);
  const translate = useTranslate();

  const query = useMediaQ('min', 1024);

  const [collapsed, setCollapsed] = useState(false);

  const inMyProjects =
    useRedux((state) => {
      const projects = state?.project?.entities?.allIds
        ?.map((id) => state?.project?.entities?.byId[id])
        ?.filter((project) => project?.theme_id === theme?.id);

      const isCreator = projects?.find(
        (project) => project?.created_by === userId
      );

      const isMember = projects?.find((project) =>
        project?.members?.find((member) => member.user_id === userId)
      );

      return isCreator || isMember;
    }) !== undefined;

  const loading = useRedux(
    (state) => state.project?.loading || state.theme?.loading
  );

  function copyProjectWithoutProcess() {
    dispatch(
      storeProject({
        withProcess: false,
        copyProject: true,
        projectId: theme?.allProjects?.[0]?.id || 0,
        themeId: theme?.id,
      })
    );
  }

  return (
    <Modal
      setModalIsOpen={setThemeModalIsOpen}
      images={
        !collapsed
          ? theme?.attachments
              .filter((a) => a.attachment_type === 'image')
              .map((a) => JSON.parse(a.attachment_data).file_path)
          : undefined
      }
      video={!collapsed ? theme?.youtube_link : undefined}
      disablePositioning={showProjectModal}
      externalRef={showProjectModal ? themeModalRef : undefined}
      collapsed
    >
      <div
        styleName="wrapper"
        style={{ alignItems: collapsed ? 'center' : 'initial' }}
      >
        <div>
          <h1>{theme?.name}</h1>
          <p styleName="author">{`${theme?.author.first_name} ${theme?.author.last_name}`}</p>
        </div>
        {((!query && !collapsed) || query) && (
          <div styleName="button-wrapper">
            {inMyProjects ? (
              <Button color="green">
                {translate('general.terms.in-my-projects')}
                <FontAwesomeIcon icon={faCheck} />
              </Button>
            ) : loading ? (
              // Prevent add if projects are still loading
              <Button color="grey" inactive>
                {translate('general.terms.in-my-projects')}
                <Spinner loading />
              </Button>
            ) : !theme?.ProjectsCount ? (
              <Button color="grey" onClick={copyProjectWithoutProcess}>
                {translate('project.actions.add-to-my-projects')}
              </Button>
            ) : (
              <TooltipMenu content={<TooltipMenuContent />}>
                <Button color="grey">
                  {translate('project.actions.add-to-my-projects')}
                </Button>
              </TooltipMenu>
            )}
          </div>
        )}
        {!collapsed && (
          <>
            <p>{theme?.description}</p>
            <LabelWrapper
              title={translate('general.terms.categories')}
              labels={theme?.categories?.map(
                (cat) => categories?.find((c) => c?.id === cat)?.name || ''
              )}
            />
          </>
        )}
        {showProjectModal && (
          <div styleName="project-modal-button">
            <Button color="grey" onClick={() => setCollapsed((prev) => !prev)}>
              {!collapsed
                ? translate('general.terms.hide-theme')
                : translate('general.terms.show-theme')}
              {!collapsed ? (
                <FontAwesomeIcon icon={faChevronUp} />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} />
              )}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
